import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import apiService from "../services/apiService";
import { LOGO_SRC } from "../constants/branding";

/**
 * Post-OAuth interstitial. Lands here when the backend redirected with
 * ?terms_pending=1 — i.e. a brand-new user signed up via the Login-page
 * Continue-with-Google button (where there's no Terms checkbox). User
 * must accept Terms to continue; newsletter is optional. On submit we
 * call /api/auth/accept-terms/ which stamps terms_accepted_at + sets
 * wants_newsletter, then push them to the dashboard with the welcome
 * toast (already armed by GoogleAuthCallback via localStorage).
 *
 * If the user closes the tab here without accepting, the next time they
 * sign in the backend will not re-set terms_pending (it only fires on
 * is_new). A future hardening pass could add a backend guard that
 * refuses key-protected endpoints until terms_accepted_at is non-null.
 */
const WelcomeTerms = () => {
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [wantsNewsletter, setWantsNewsletter] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms || isSaving) return;
    setIsSaving(true);
    try {
      await apiService.acceptTerms({ wantsNewsletter });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Could not save your preferences. Please try again.");
      setIsSaving(false);
    }
  };

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[min(50vh,400px)] w-[min(80vw,560px)] rounded-full bg-emerald-500/[0.05] blur-[90px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="rounded-sm border border-white/10 bg-landing-section p-8 sm:p-10">
            <div className="mb-8 text-center">
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-6 h-10 w-auto sm:h-11"
              />
              <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                One more step
              </h2>
              <p className="font-secondary mt-2 text-[17px] leading-relaxed text-white/65">
                Accept our terms to finish setting up your account.
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <label className="flex items-start gap-3 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={wantsNewsletter}
                  onChange={(e) => setWantsNewsletter(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-white/30 bg-[#0c0c0c] text-primary focus:ring-2 focus:ring-primary/50 focus:ring-offset-0 cursor-pointer"
                />
                <span className="font-secondary text-sm leading-relaxed text-white/75 group-hover:text-white/90 transition-colors">
                  Subscribe me to product updates and the DataNodes newsletter.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-white/30 bg-[#0c0c0c] text-primary focus:ring-2 focus:ring-primary/50 focus:ring-offset-0 cursor-pointer"
                />
                <span className="font-secondary text-sm leading-relaxed text-white/75 group-hover:text-white/90 transition-colors">
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary-hover underline underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </Link>
                  {" "}and{" "}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary-hover underline underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>

              <motion.button
                type="submit"
                disabled={!acceptedTerms || isSaving}
                className="font-secondary mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                whileHover={acceptedTerms && !isSaving ? { scale: 1.01 } : {}}
                whileTap={acceptedTerms && !isSaving ? { scale: 0.99 } : {}}
              >
                {isSaving ? (
                  <>
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#0a0a0a] border-t-transparent"
                      aria-hidden="true"
                    />
                    Continuing…
                  </>
                ) : (
                  "Continue to DataNodes"
                )}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WelcomeTerms;
